/**
 * Filters on the whole crosstab (M4S-5777 X39, X40, X41, X43, X57): what one means, who it keeps,
 * and the words every table and file prints for it. The one home of the filter rule: compute calls
 * `planFilters` and nothing else decides who a filter keeps (`live-filters.guard.test.ts`). Pure.
 * Spec: docs/pdlc/crosstabs-filters.md.
 *
 * ### What a filter keeps
 *
 * A filter names a survey question (a wave variant's own qid included), the survey (`__SURVEY_ID`)
 * or a custom variable, and keeps (`include`) or removes (`include: false`) the respondents who:
 *
 * - `'answers'` (absent = this, every filter saved before X39): hold ANY of `optionIds`. A survey
 *   answer resolves to every code a merged wave gave it (`holdsCode`), as before; a variable's
 *   option is one of its groups, by label (`variableAnswerSets`, the banner's own rule).
 * - `'answered'`: gave any answer to the question (`answeredAny`, conditions.ts). The data holds a
 *   row only when a question is answered, so "not answered" is also "not asked" (conditions §2.3).
 * - `'range'`: hold a number from `range.min` to `range.max`, both inclusive, a missing bound open.
 *   Number questions only (decision 4): it is two comparisons, ≥ min and ≤ max, resolved and
 *   evaluated by `evalCondition` under today's rules, so Don't know coded 98 is never "at least 9".
 *
 * Removing keeps everyone else, the people who did not answer included: "NOT Q2 is 2" keeps those
 * never asked Q2, as it always has.
 *
 * ### How filters join
 *
 * Each filter's `nextJoin` joins it to the next (absent = AND), and AND binds tighter than OR
 * (`join-fold.ts`): "A AND B OR C" is (A AND B) OR C. A filter tab (X41) is ANDed with the whole
 * list, whatever it holds, so a tab never splits an OR.
 *
 * ### A filter on something that is not loaded
 *
 * A filter on a question no loaded survey has and nobody answered, or on a variable that is no
 * longer in the crosstab, is skipped and reported (`FilterPlan.skipped`), and the workspace says so.
 * It used to empty the table (keep) or do nothing (remove) without a word. Declared in the spec §7.
 */

import type {
    CustomVariable,
    FilterTabs,
    LiveFilter,
    VariableBucket,
} from '@/app/(private)/ai/components/Crosstabs/crosstab-types';
import { holdsCode, type OptionCodes } from '@/app/(private)/ai/components/Crosstabs/option-codes';

import {
    CURRENT_RULES,
    answeredAny,
    comparisonKindOf,
    evalCondition,
    questionLookup,
    resolveBucketConditions,
    type Answers,
} from './conditions';
import { hasVariableGroups, variableAnswerSets, variableGroupLabels } from './custom-var-answers';
import { joinGroups, type Join } from './join-fold';
import { formatCount } from './number-format';
import type { CrosstabQuestionDto } from './question-formatter';

/** The survey a respondent answered, as a filter names it. */
export const SURVEY_FILTER_ID = '__SURVEY_ID';

export type FilterScope = 'answers' | 'answered' | 'range';

export function filterScopeOf(f: Pick<LiveFilter, 'scope'>): FilterScope {
    return f.scope === 'answered' || f.scope === 'range' ? f.scope : 'answers';
}

// ── Parsing ───────────────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const finite = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;

/**
 * One filter in its canonical shape, or null. The fields every filter has come first in the order
 * the rail has always written them, and the X39 fields only when they say something, so a filter
 * saved before X39 parses to itself and an opened crosstab signs as saved. A filter with no answer
 * picked is kept as it was saved (it keeps no one, or removes no one), so no saved crosstab moves.
 */
export function parseLiveFilter(raw: unknown, fallbackIndex = 0): LiveFilter | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const r = raw as Record<string, unknown>;
    const questionId = str(r.questionId)?.trim();
    if (!questionId) return null;
    // A filter stored without an id (an old or hand-made one) still applies: it is named for its
    // question and place, so it is never silently dropped.
    const id = str(r.id)?.trim() || `${questionId}_${fallbackIndex}`;
    const optionIds = Array.isArray(r.optionIds)
        ? r.optionIds.filter((x): x is string => typeof x === 'string')
        : [];
    const labels = Array.isArray(r.optionLabels) ? r.optionLabels : [];
    const optionLabels = optionIds.map((oid, i) =>
        typeof labels[i] === 'string' ? (labels[i] as string) : oid,
    );
    const scope = filterScopeOf({ scope: r.scope as LiveFilter['scope'] });
    const rawRange =
        r.range && typeof r.range === 'object' ? (r.range as Record<string, unknown>) : {};
    const min = finite(rawRange.min);
    const max = finite(rawRange.max);
    if (scope === 'range' && min === undefined && max === undefined) return null;
    if (scope === 'range' && min !== undefined && max !== undefined && min > max) return null;
    const savedFilterId = str(r.savedFilterId)?.trim();
    return {
        id,
        questionId,
        questionLabel: str(r.questionLabel) ?? questionId,
        optionIds: scope === 'answers' ? optionIds : [],
        optionLabels: scope === 'answers' ? optionLabels : [],
        include: r.include !== false,
        ...(scope !== 'answers' ? { scope } : {}),
        ...(scope === 'range'
            ? {
                  range: {
                      ...(min !== undefined ? { min } : {}),
                      ...(max !== undefined ? { max } : {}),
                  },
              }
            : {}),
        ...(r.nextJoin === 'any' ? { nextJoin: 'any' as const } : {}),
        ...(savedFilterId ? { savedFilterId } : {}),
    };
}

/** A filter list from storage or a request: garbage dropped, ids unique (the first one kept). */
export function parseLiveFilters(raw: unknown): LiveFilter[] {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const out: LiveFilter[] = [];
    for (const [i, item] of raw.entries()) {
        const f = parseLiveFilter(item, i);
        if (!f || seen.has(f.id)) continue;
        seen.add(f.id);
        out.push(f);
    }
    return out;
}

// ── Editing (X40) ─────────────────────────────────────────────────────────────

/**
 * The list with filter `id` replaced by `next`, in place: same id, same position, same join to the
 * next filter (unless `next` sets its own). Every other filter is returned as it was (reference
 * equal). Unknown id: the list unchanged.
 */
export function replaceFilter(list: LiveFilter[], id: string, next: LiveFilter): LiveFilter[] {
    const at = list.findIndex((f) => f.id === id);
    if (at < 0) return list;
    const was = list[at];
    const join = next.nextJoin ?? was.nextJoin;
    const edited: LiveFilter = { ...next, id: was.id };
    delete edited.nextJoin;
    if (join === 'any') edited.nextJoin = 'any';
    return list.map((f, i) => (i === at ? edited : f));
}

/** The list with the join after filter `index` set (AND is stored as no key). */
export function withFilterJoin(list: LiveFilter[], index: number, join: Join): LiveFilter[] {
    if (index < 0 || index >= list.length - 1) return list;
    return list.map((f, i) => {
        if (i !== index) return f;
        const rest = { ...f };
        delete rest.nextJoin;
        return join === 'any' ? { ...rest, nextJoin: 'any' as const } : rest;
    });
}

/** The list without filter `id`. */
export function removeFilter(list: LiveFilter[], id: string): LiveFilter[] {
    return list.filter((f) => f.id !== id);
}

/** Whether any filter but the last joins the next with OR. */
export function usesOr(list: readonly LiveFilter[]): boolean {
    return list.some((f, i) => i < list.length - 1 && f.nextJoin === 'any');
}

/**
 * The list with a saved filter's parts appended (X44), ANDed onto what is there, each part a copy
 * with its own id that remembers the saved filter it came from. A flat list cannot say
 * "X AND (A OR B)", so a saved filter that uses OR is applied only to a crosstab with no filters,
 * and one without OR only onto filters without OR; anything else is refused with the words to show.
 */
export function appendSavedFilter(
    list: LiveFilter[],
    saved: { id: string; name: string; filters: readonly LiveFilter[] },
    idSeed: string,
): { filters: LiveFilter[] } | { refused: string } {
    if (saved.filters.length === 0) return { refused: `"${saved.name}" holds no filters.` };
    if (list.length > 0 && (usesOr(saved.filters) || usesOr(list))) {
        return {
            refused: `"${saved.name}" can't be added to these filters: one of them uses OR, so the result would be ambiguous. Clear the filters first, or add its parts one at a time.`,
        };
    }
    const parts = saved.filters.map((f, i) => {
        const copy: LiveFilter = {
            ...f,
            id: `${f.questionId}_${idSeed}_${i}`,
            savedFilterId: saved.id,
        };
        if (i === saved.filters.length - 1) delete copy.nextJoin;
        return copy;
    });
    const head = list.map((f, i) => {
        if (i !== list.length - 1 || f.nextJoin === undefined) return f;
        const rest = { ...f };
        delete rest.nextJoin;
        return rest;
    });
    return { filters: [...head, ...parts] };
}

// ── Who a filter keeps ────────────────────────────────────────────────────────

export interface FilterContext {
    /** Every respondent's answers, as compute parses them (upper-cased keys). */
    responses: ReadonlyArray<Answers>;
    /** Every code of each multi-code option (`optionCodesOf`). */
    codes: OptionCodes;
    questions: readonly CrosstabQuestionDto[];
    /** The crosstab's variables, resolved as compute resolves them (`resolveConditions`). */
    variables: readonly CustomVariable[];
}

export type SkipReason = 'not-loaded' | 'no-groups' | 'not-a-number-question';

export interface SkippedFilter {
    id: string;
    name: string;
    reason: SkipReason;
}

export interface FilterPlan {
    /** Whether any filter (or the slice) applies. False: everyone is kept. */
    active: boolean;
    /** Whether respondent `i` is kept. */
    keeps: (i: number) => boolean;
    /** The filters that apply, in order. */
    applied: LiveFilter[];
    /** The filters that could not apply, and why. */
    skipped: SkippedFilter[];
    /** Test one more filter against respondent `i` (a tab's count, X41); null when it is skipped. */
    testerFor: (f: LiveFilter) => ((i: number) => boolean) | null;
}

/** Whether a filter names one of the crosstab's variables. */
function variableOf(
    f: LiveFilter,
    variables: readonly CustomVariable[],
): CustomVariable | undefined {
    return variables.find((v) => v.id === f.questionId);
}

/** The words a filter's subject reads as: a question's QID, "Survey", or a variable's name. */
export function filterSubjectName(
    f: Pick<LiveFilter, 'questionId' | 'questionLabel'>,
    variables: readonly Pick<CustomVariable, 'id' | 'name'>[] = [],
): string {
    if (f.questionId === SURVEY_FILTER_ID) return 'Survey';
    const cv = variables.find((v) => v.id === f.questionId);
    if (cv) return cv.name;
    return f.questionId;
}

/**
 * Builds the test for one filter, or says why it cannot apply. Every respondent's matching work is
 * done once here (a variable's groups, a range's resolved comparisons), not per respondent.
 */
function compileFilter(
    f: LiveFilter,
    ctx: FilterContext,
    find: (qid: string) => CrosstabQuestionDto | undefined,
): { test: (i: number) => boolean } | { skip: SkipReason } {
    const scope = filterScopeOf(f);
    const key = f.questionId.trim().toUpperCase();
    const cv = variableOf(f, ctx.variables);
    let matches: (i: number) => boolean;

    if (cv) {
        if (!hasVariableGroups(cv)) return { skip: 'no-groups' };
        if (scope === 'range') return { skip: 'not-a-number-question' };
        const sets = variableAnswerSets(cv, ctx.responses);
        const wanted = f.optionIds;
        matches =
            scope === 'answered'
                ? (i) => sets[i].size > 0
                : (i) => wanted.some((label) => sets[i].has(label));
    } else {
        const q = key === SURVEY_FILTER_ID ? undefined : find(key);
        const held = (i: number) => (ctx.responses[i][key]?.size ?? 0) > 0;
        if (key !== SURVEY_FILTER_ID && !q && !ctx.responses.some((_, i) => held(i))) {
            return { skip: 'not-loaded' };
        }
        if (scope === 'answered') {
            matches = (i) => answeredAny(ctx.responses[i], [key]);
        } else if (scope === 'range') {
            if (comparisonKindOf(q) !== 'value') return { skip: 'not-a-number-question' };
            const bucket: VariableBucket = {
                label: '',
                join: 'all',
                conditions: [
                    ...(f.range?.min !== undefined
                        ? [
                              {
                                  questionId: key,
                                  operator: 'gte' as const,
                                  value: String(f.range.min),
                              },
                          ]
                        : []),
                    ...(f.range?.max !== undefined
                        ? [
                              {
                                  questionId: key,
                                  operator: 'lte' as const,
                                  value: String(f.range.max),
                              },
                          ]
                        : []),
                ],
            };
            const [resolved] = resolveBucketConditions([bucket], ctx.questions);
            matches = (i) =>
                resolved.conditions.every((c) => evalCondition(ctx.responses[i], c, CURRENT_RULES));
        } else {
            const ids = f.optionIds;
            matches = (i) => {
                const selected = ctx.responses[i][key];
                return ids.some((id) => holdsCode(selected, key, id, ctx.codes));
            };
        }
    }
    return { test: f.include ? matches : (i) => !matches(i) };
}

/**
 * Who `filters` keep, ANDed with `slice` (a filter tab) when one is given. Filters that cannot apply
 * are left out of the fold and reported; a slice that cannot apply keeps no one, so a tab on a
 * question that is gone shows an empty table rather than the whole sample.
 */
export function planFilters(
    filters: readonly LiveFilter[] | undefined,
    ctx: FilterContext,
    slice?: LiveFilter | null,
): FilterPlan {
    const find = questionLookup(ctx.questions);
    const applied: { f: LiveFilter; test: (i: number) => boolean }[] = [];
    const skipped: SkippedFilter[] = [];
    for (const f of filters ?? []) {
        const c = compileFilter(f, ctx, find);
        if ('skip' in c) {
            skipped.push({ id: f.id, name: filterSubjectName(f, ctx.variables), reason: c.skip });
        } else {
            applied.push({ f, test: c.test });
        }
    }
    const groups = joinGroups(applied, (a) => a.f.nextJoin);
    const sliceTest = slice
        ? (() => {
              const c = compileFilter(slice, ctx, find);
              return 'skip' in c ? () => false : c.test;
          })()
        : null;
    const keeps = (i: number) =>
        (applied.length === 0 || groups.some((g) => g.every((a) => a.test(i)))) &&
        (!sliceTest || sliceTest(i));
    return {
        active: applied.length > 0 || !!sliceTest,
        keeps,
        applied: applied.map((a) => a.f),
        skipped,
        testerFor: (f) => {
            const c = compileFilter(f, ctx, find);
            return 'skip' in c ? null : c.test;
        },
    };
}

// ── Words (X43) ───────────────────────────────────────────────────────────────

/** "A", "A or B", "A, B or C". */
function orList(items: readonly string[]): string {
    if (items.length <= 1) return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

function rangeWords(range: LiveFilter['range']): string {
    const { min, max } = range ?? {};
    if (min !== undefined && max !== undefined) return min === max ? `${min}` : `${min} to ${max}`;
    if (min !== undefined) return `at least ${min}`;
    return `at most ${max}`;
}

/**
 * One filter in words: "AGE is 18-24 or 25-34", "NOT GENDER is Male", "QSPEND answered". `subject`
 * names what is filtered when the caller has a better name than the QID (the rail's chips use the
 * question's wording).
 */
export function describeFilter(
    f: LiveFilter,
    variables: readonly Pick<CustomVariable, 'id' | 'name'>[] = [],
    subject?: string,
): string {
    const name = subject ?? filterSubjectName(f, variables);
    const scope = filterScopeOf(f);
    if (scope === 'answered') return f.include ? `${name} answered` : `${name} not answered`;
    // A filter handed to compute directly may carry no labels: its answer ids stand in.
    const labels =
        Array.isArray(f.optionLabels) && f.optionLabels.length
            ? f.optionLabels
            : (f.optionIds ?? []);
    const what = scope === 'range' ? rangeWords(f.range) : orList(labels);
    return `${f.include ? '' : 'NOT '}${name} is ${what}`;
}

/**
 * The filters in words, joined as they are evaluated: "A AND B OR C" reads "(A AND B) OR C", so
 * the grouping is never left to the reader. Empty for no filters.
 */
export function describeFilters(
    filters: readonly LiveFilter[],
    variables: readonly Pick<CustomVariable, 'id' | 'name'>[] = [],
): string {
    const groups = joinGroups(filters, (f) => f.nextJoin);
    const parts = groups.map((g) => {
        const text = g.map((f) => describeFilter(f, variables)).join(' AND ');
        return groups.length > 1 && g.length > 1 ? `(${text})` : text;
    });
    return parts.join(' OR ');
}

/**
 * The line every table and file prints for a filtered table: what it is filtered to and the base
 * it leaves. A tab's own answer is ANDed after the crosstab's filters. Null when nothing filters.
 */
export function filterNoteOf(args: {
    filters: readonly LiveFilter[];
    slice?: LiveFilter | null;
    variables?: readonly Pick<CustomVariable, 'id' | 'name'>[];
    base: number;
}): string | null {
    const { filters, slice, variables = [], base } = args;
    if (!filters.length && !slice) return null;
    const whole = describeFilters(filters, variables);
    const wrapped = whole && slice && whole.includes(' OR ') ? `(${whole})` : whole;
    const text = [wrapped, slice ? describeFilter(slice, variables) : '']
        .filter(Boolean)
        .join(' AND ');
    return `Filtered to: ${text} (n=${formatCount(base)})`;
}

/** What the workspace says when filters could not apply. */
export function skippedFiltersNotice(skipped: readonly SkippedFilter[]): string {
    const why: Record<SkipReason, string> = {
        'not-loaded': 'its question or variable is not in this crosstab',
        'no-groups': 'its variable has no groups to filter by',
        'not-a-number-question': 'a number range needs a number question',
    };
    const lines = skipped.map((s) => `"${s.name}" (${why[s.reason]})`);
    return skipped.length === 1
        ? `A filter was not applied: ${lines[0]}. Edit or remove it.`
        : `${skipped.length} filters were not applied: ${lines.join('; ')}. Edit or remove them.`;
}

// ── Filter tabs (X41) ─────────────────────────────────────────────────────────

/** A filter tab setting in its one shape, or null. */
export function parseFilterTabs(raw: unknown): FilterTabs | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const r = raw as Record<string, unknown>;
    const questionId = str(r.questionId)?.trim();
    if (!questionId) return null;
    return { questionId, label: str(r.label) ?? questionId };
}

/** The groups a filter can pick from: a catalog question's answers, the surveys, or a variable's groups. */
export interface FilterSourceDef {
    label: string;
    cats: ReadonlyArray<{ id: string; label: string }>;
}

/** A variable's groups as a filter source: each group's label is its id (custom-var-answers.ts). */
export function variableFilterSource(cv: CustomVariable): FilterSourceDef | null {
    if (!hasVariableGroups(cv)) return null;
    return { label: cv.name, cats: variableGroupLabels(cv).map((l) => ({ id: l, label: l })) };
}

/** The id of the filter a tab applies. */
export function sliceFilterId(questionId: string, optionId: string): string {
    return `tab:${questionId}:${optionId}`;
}

/**
 * One keep-filter per group of the tab source, in the source's order: the tabs after Total. Empty
 * when the source is not available (its question is not loaded, its variable is gone).
 */
export function filterTabSlices(
    tabs: FilterTabs | null,
    source: FilterSourceDef | null,
): LiveFilter[] {
    if (!tabs || !source) return [];
    return source.cats.map((c) => ({
        id: sliceFilterId(tabs.questionId, c.id),
        questionId: tabs.questionId,
        questionLabel: source.label,
        optionIds: [c.id],
        optionLabels: [c.label],
        include: true,
    }));
}
